import React, {useMemo} from 'react';
import {APIProvider} from "@vis.gl/react-google-maps";
import {FaArrowLeft, FaPhone} from "react-icons/fa6";
import IndividualAlarmMap from "./individual-alarm-map.jsx";
import IndividualAlarmMatches from "./individual-alarm-matches.jsx";
import Button from "../../../../components/button/button.jsx";
import projectUtils from "../../../../utils/project-utils.js";
import {navigateHook} from "../../../../hooks/use-navigate.js";
import {getAssetPath} from "../../../../utils/get-asset-path.js";
import IndividualAlarmService from "../../../../services/web/individual-alarm-service.js";
import {SIGN_ERRORS} from "../utils/individual-alarm-utils.js";
import {toast} from "react-toastify";

const API_DESPACHO_WEB = import.meta.env.VITE_API_DESPACHO_WEB;

const IndividualAlarmDetails = ({
                                    latitude,
                                    longitude,
                                    vmsConfigurations,
                                    hasSignPermission,
                                    selected,
                                    mapsKey,
                                    openModal,
                                    secondScrollRef,
                                    setIsLoading,
                                    search,
                                    setSelectedAlarmId,
                                    returnToDefault,
                                    hasDispatch
                                }) => {
    const navigate = navigateHook();

    const {individualMatchesModels, individualDocument, documentType, id, signed, captureTime, relationship} = selected;

    const mapMarkers = useMemo(() => individualMatchesModels
        ?.filter((item) => item?.data?.latitude != null && item?.data?.longitude != null)
        .map((item) => ({lat: item?.data?.latitude, lng: item?.data?.longitude})), [individualMatchesModels]);

    const openGis = () => {
        if (mapMarkers?.length > 0) {
            const {lat, lng} = mapMarkers[0];
            const url = `/gis?latLng=${lat},${lng}&id=`;
            navigate(url, true);
        }
    };

    const handleSign = () => {
        setIsLoading(true);

        IndividualAlarmService.sign(id)
            .then(async () => {
                toast.success("Assinado com sucesso");
                await search();
                setSelectedAlarmId(id);
            })
            .catch(error => {
                let message = "Erro ao assinar o alarme.";
                if (error?.response?.data?.i18nMessage) message = SIGN_ERRORS[error.response.data.i18nMessage] ?? message;
                projectUtils.swalErrorMessage('', message, 'Ok');
                setIsLoading(false);
            });
    };

    const handleAttendance = () => {
        let url = `/despacho/attendance/form/${relationship?.attendance}`;

        if (!relationship || !relationship.attendance) {
            url = `/despacho/attendance/form?individualAlarmId=${id}`;
        }

        navigate(url, true);
    };

    const handleOccurrence = () => {
        if (!relationship || !relationship.occurrence) {
            projectUtils.createOccurrence(id, "/individual-alarm/create-occurrence");
            return;
        }

        projectUtils.openOccurrence(relationship.occurrence);
    };

    const actionButtons = (
        <div className={"flex justify-start gap-2"}>
            <Button target={"primary-outline"} title={"Abrir mapa em tempo real"} className={"w-[45px] p-0!"} onClick={openGis}>
                <img src={getAssetPath("img/custom_icons/gis-icon.svg")} alt="gis-icon"/>
            </Button>
            <Button
                target={relationship?.occurrence ? "primary" : "primary-outline"}
                className={"w-[45px] p-0!"}
                title={relationship?.occurrence ? "Abrir ocorrência" : "Criar ocorrência"}
                onClick={handleOccurrence}
            >
                <img
                    src={getAssetPath("img/custom_icons/" + (relationship?.occurrence ? "sentry.occurrence.white.svg" : "sentry.occurrence.svg"))}
                    alt={"sentry.occurrence"}
                />
            </Button>

            {
                hasDispatch &&
                <Button
                    target={relationship?.attendance ? "primary" : "primary-outline"}
                    title={relationship?.attendance ? "Abrir atendimento" : "Criar atendimento"}
                    onClick={handleAttendance}
                    className={"w-[45px] p-0!"}
                >
                    <FaPhone/>
                </Button>
            }
        </div>
    );

    return (
        <div className={"w-full flex h-full"}>
            <div className={"w-3/4"}>
                {
                    mapMarkers &&
                    <div className={"ia-map h-full"}>
                        <APIProvider apiKey={mapsKey}>
                            <IndividualAlarmMap
                                initialPos={{lat: Number(latitude), lng: Number(longitude)}}
                                id={id}
                                markers={mapMarkers}
                            />
                        </APIProvider>
                    </div>
                }
            </div>

            {
                individualMatchesModels &&
                <div className={"w-1/4 flex flex-col justify-between pl-2 h-full"}>
                    <div ref={secondScrollRef} className={"flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden"}>
                        <span className={"text-[20px] cursor-pointer mr-4"} onClick={() => returnToDefault()}>
                            <FaArrowLeft/>
                        </span>

                        <div>
                            {
                                individualMatchesModels.map((item) => (
                                    <div key={item.id} className={"w-full"}>
                                        <IndividualAlarmMatches
                                            vmsConfigurations={vmsConfigurations}
                                            captureTime={captureTime}
                                            individualMatchesModel={item}
                                            individualDocument={individualDocument}
                                            individualDocumentType={documentType}
                                            openModal={openModal}
                                        />
                                    </div>
                                ))
                            }
                        </div>
                    </div>

                    <div>
                        {
                            signed &&
                            <>
                                {actionButtons}
                                <div className={"mt-2 p-2 bg-white rounded-[0.5rem] border border-[#ededed] w-full flex text-center flex-col"}>
                                    <h4 className={"m-0 font-normal!"}>Assinado por <b>{signed?.userName}</b> em </h4>
                                    <h4 className={"m-0 font-normal!"}>
                                        <b>{projectUtils.formatDate(signed.signedAt, "DD/MM/YYYY")}</b> ás <b>{projectUtils.formatTimeString(new Date(signed.signedAt))}</b>
                                    </h4>
                                </div>
                            </>
                        }
                        <div className={"flex"}>
                            {!signed && actionButtons}
                            {
                                hasSignPermission && id && !signed &&
                                <Button
                                    target={"primary"}
                                    className={"w-full p-3 ml-2"}
                                    onClick={handleSign}
                                    actionFeedbackType="save"
                                    loadingText="Assinando..."
                                    successText="Assinado com sucesso"
                                >
                                    Assinar
                                </Button>
                            }
                        </div>
                    </div>
                </div>
            }
        </div>
    );
};

export default IndividualAlarmDetails;